// router/index.js — vue-router (v3) instance + the authentication guard.
// Purpose: define the app's routes (names/paths VERBATIM from inventory/routes.md)
// and install the `router.beforeEach` guard exactly as the deployed build does in
// app.js / main-app/a18c.js: public routes (meta.public) bypass; everything else
// requires a `session_key` cookie (or localStorage flag), otherwise the user is
// bounced to `/login?redirect=<fullPath>` (root '/' redirects without a query).
// Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
// (guard logic, `meta.public`, `/login`, `redirect`, route names — AUTH-SESSION §2.4,
// inventory/routes.md), internal impl re-authored.

import Vue from 'vue'
import VueRouter from 'vue-router'
import { getCookie, SESSION_COOKIE } from '@/shell/session'

// View components — minimal but real screens that USE the Tier-1 engines.
import LoginView from '@/views/LoginView.vue'
import MonitoringView from '@/views/MonitoringView.vue'
import ReportView from '@/views/ReportView.vue'
import EntityView from '@/views/EntityView.vue'
import DevicesView from '@/views/DevicesView.vue'
import ServerErrorView from '@/views/ServerErrorView.vue'
import NotFoundView from '@/views/NotFoundView.vue'

Vue.use(VueRouter)

const routes = [
  // ---- public routes (meta.public bypasses the session guard) -----------------
  // The Vue app owns the login screen + account lifecycle (AUTH-SESSION §1, §4).
  { path: '/login', name: 'login', component: LoginView, meta: { public: true } },
  { path: '/signup', name: 'signup', component: LoginView, meta: { public: true, mode: 'signup' } },
  { path: '/restore', name: 'restore', component: LoginView, meta: { public: true, mode: 'restore' } },
  { path: '/activation', name: 'activation', component: LoginView, meta: { public: true, mode: 'activation' } },
  { path: '/register', name: 'promo-register', component: LoginView, meta: { public: true, mode: 'register' } },

  // ---- authenticated app ------------------------------------------------------
  // '/' is the live monitoring page (component `online` in the deployed build).
  // `/online/trackers` is the deployed app's real path for the same screen, kept as
  // an alias so the fidelity path (and its deep-links below) resolve.
  { path: '/', name: 'root', component: MonitoringView, alias: '/online/trackers' },

  // Track-history deep-link — the real go.lacak.io URL shape
  //   /#/online/trackers/trips/tracks/{trackerId}/{fromISO}/{toISO}
  // e.g. /#/online/trackers/trips/tracks/100/2026-07-07T00:00:00.000Z/2026-07-08T00:00:00.000Z
  // Opens MonitoringView and drives its history panel straight from the URL. The short
  // `/trips/tracks/...` alias mirrors the form the doc also lists (HISTORY-FIDELITY §route).
  {
    path: '/online/trackers/trips/tracks/:id/:from/:to',
    name: 'trips-tracks',
    component: MonitoringView,
    alias: '/trips/tracks/:id/:from/:to'
  },

  // Interactive report viewer — registers the `report-view` module and fetches
  // report/tracker/retrieve by :id (HISTORY-REPORTS §1a).
  { path: '/reports/:id?', name: 'report-view', component: ReportView },

  // Generic entity CRUD — the config-driven entity-form engine (ENTITY-BUILDER).
  { path: '/entity/:type/:id?', name: 'entity-view', component: EntityView },

  // Device-settings schema portlet lives in a Tier-2 engine; placeholder host.
  { path: '/devices', name: 'devices', component: DevicesView },

  // Landing target for the global error table on backend status.code 12
  // (the request layer pushes '/server-error'; AUTH-SESSION §2.3). Public so the
  // page is reachable even when the session was just invalidated.
  { path: '/server-error', name: 'server-error', component: ServerErrorView, meta: { public: true } },

  // ---- fallback ---------------------------------------------------------------
  { path: '*', name: 'fallback', component: NotFoundView }
]

const router = new VueRouter({
  mode: 'hash', // deployed pro-ui uses hash routing (Config.appRedirect.loginPage = origin + '/#/login')
  routes
})

// The session guard — byte-faithful to AUTH-SESSION §2.4.
router.beforeEach((to, from, next) => {
  const authed =
    to.matched.some((r) => r.meta && r.meta.public) ||
    getCookie(SESSION_COOKIE) ||
    localStorageGet(SESSION_COOKIE)

  if (authed) {
    next()
  } else {
    // eslint-disable-next-line no-console
    console.log('Not authenticated')
    next({
      path: '/login',
      query: to.fullPath === '/' ? undefined : { redirect: to.fullPath }
    })
  }
})

function localStorageGet(key) {
  try {
    return window.localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

export default router
